/**
 * Live check of the password policy, for the checklist under a password field.
 *
 * The SERVER is the judge: the backend publishes the policy (`GET /password-policy/`, four rule
 * codes) and rejects a bad password with `password_policy_failed` plus the failed codes, and its
 * contract says "do not add client-only rules". This file only mirrors those four rules so the
 * checklist can tick as the person types; it must follow `failed_password_rules` in the backend's
 * `identity/password_policy.py`:
 *   - length counts characters, not UTF-16 units (`[...password]`, like Python's `len`);
 *   - the input is never trimmed;
 *   - a symbol is any character that is not a letter, a number or whitespace.
 * Python's `isdigit` also accepts a few superscript digits that `\p{Nd}` does not; an edge the
 * server answer corrects.
 */
export const PASSWORD_RULE_CODES = [
  "min_length",
  "digit",
  "uppercase",
  "symbol",
] as const;

export type PasswordRuleCode = (typeof PASSWORD_RULE_CODES)[number];

export const PASSWORD_MIN_LENGTH = 8;

const CHECKS: Record<PasswordRuleCode, (password: string) => boolean> = {
  min_length: (password) => [...password].length >= PASSWORD_MIN_LENGTH,
  digit: (password) => /\p{Nd}/u.test(password),
  uppercase: (password) => /\p{Lu}/u.test(password),
  symbol: (password) => /[^\p{L}\p{N}\s]/u.test(password),
};

/** Whether each rule is met by `password`. */
export function checkPasswordRules(
  password: string
): Record<PasswordRuleCode, boolean> {
  return {
    min_length: CHECKS.min_length(password),
    digit: CHECKS.digit(password),
    uppercase: CHECKS.uppercase(password),
    symbol: CHECKS.symbol(password),
  };
}

/** The rules `password` fails, in policy order. Empty means it satisfies the policy. */
export function failedPasswordRules(password: string): PasswordRuleCode[] {
  return PASSWORD_RULE_CODES.filter((code) => !CHECKS[code](password));
}

/** True when `password` satisfies all four rules. */
export function isPasswordValid(password: string): boolean {
  return failedPasswordRules(password).length === 0;
}
